import * as _ from 'lodash'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'

import LogoSvg from '@assets/images/logo/svg/logo_whiteblue.svg'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'

import MenuBar from 'layout/components/menu/bar/menu-bar'
import MenuHeader from 'layout/components/menu/header/menu-header'
import MenuFooter from 'layout/components/menu/footer/menu-footer'

import { user } from 'ampersand-app'
import {
  resetTemporaryActiveMenuItem,
  setSubmenuState,
  toggleNotificationOverlay,
  toggleSearchOverlay,
  toggleMenuOverlay,
} from '@/redux/ducks/menu/main'

import './menu.global.css'
import SubmenuBar from '../submenu/bar/submenu-bar'
import MenuOverlay from './overlay'
import GlobalSearch from './overlay/globalSearch'
import { isMobileScreen } from '@/modules/_helpers/detectMobile'
import SwipeDrawer from '@/components/drawers/swipe-drawer'
import { SubmenuFooter } from '../submenu/footer/submenu-footer'
import UserHeader from './header/user-header'
import clsx from 'clsx'
import moment from 'moment'
import TopPanel from '@/modules/base/components/top-panel/top-panel'
import { isMeinBuero } from '@/utils/build-env'
import lang from '@/lang'
import MenuV2 from '@/modules/layout/components/menuV2/menu'
import Notifications from '@/modules/base/components/notification/overlay/notifications'
import NotificationDrawer from '@/modules/base/components/notification/drawer/notification-drawer'
import SearchDrawer from '@/modules/base/components/search/drawer/search-drawer'
import { useUpgradeModal } from '@/hooks/useUpgradeModal'
import { useNavigate } from 'react-router'
import GtmService, { GtmEventTypes } from '@/modules/_services/gtm.service'

const { product } = ENV

const useStyles = makeStyles(theme => ({
  [theme.devices.mobile]: {
    hamburger: {
      color: 'white',
      display: 'flex',
      flexDirection: 'column',
      height: 18,
      alignItems: 'center',
      justifyContent: 'space-between',
      margin: '0 20px',
    },
    span: {
      width: 18,
      height: '2px',
      position: 'relative',
      background: '#ccc',
      transformOrigin: '4px 0px',
      transition: 'transform 0.5s cubic-bezier(0.77, 0.2, 0.05, 1.0), background 0.5s cubic-bezier(0.77, 0.2, 0.05, 1.0), opacity 0.55s ease',
      '&:first-child': {
        transformOrigin: '0% 0%',
      },
      '&:nth-last-child(2)': {
        transformOrigin: '0% 100%',
      },
    },
    selected: {
      '& > $span': {
        opacity: 1,
        transform: 'rotate(45deg) translate(-6px, -8px)',
        '&:nth-last-child(3)': {
          opacity: 0,
          transform: 'rotate(0deg) scale(0.2, 0.2)',
        },
        '&:nth-last-child(2)': {
          transform: 'rotate(-45deg) translate(-4px, 7px)',
        },
      },
    },
    logo: ({ isMeinBuero }) => {
      return {
        transform: 'translateX(0vw)',
        transition: 'transform 0.4s ease',
        width: isMeinBuero ? 117 : 78,
      }
    },
    logoSlide: ({ isMeinBuero }) => {
      return {
        width: isMeinBuero ? 117 : 98,
        transform: `translateX(${isMeinBuero ? 60 : 55}vw)`,
      }
    },
    fadeOut: {
      opacity: 0,
    },
    mobileContainer: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    upgradeTopPanel: ({ isTrial }) => {
      return {
        background: isTrial ? theme.mobileNav.demoHeader : theme.redStateColor,
      }
    },
    button: ({ isTrial }) => {
      return {
        background: !isTrial && 'transparent',
        border: !isTrial && theme.border,
        height: 'auto',
        fontSize: theme.fontSizes.tiny,
        margin: '3px 0',
        padding: '3px 8px',
      }
    },
    mobileNav: {
      display: 'flex',
      justifyContent: 'space-between',
      background: theme.navBackgroundColor,
      height: 50,
      position: 'relative',
      width: '100%',
      '&> div': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
      },
    },
    menuContainer: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    subMenuOverlay: {
      zIndex: 1201,
    },
    menuDrawer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      height: '100%',
      background: theme.navBackgroundColor,
      alignItems: 'space-around',
    },
    paperClass: ({ isTopPanelVisible }) => {
      return {
        top: isTopPanelVisible ? 75 : 50,
        height: `calc(100% - ${isTopPanelVisible ? 75 : 50}px)`,
      }
    },
    imprint: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      margin: 20,
      padding: '20px 0',
      borderTop: theme.border,
      borderColor: theme.transparentBorder,
    },
    header: {
      borderTop: theme.border,
      borderBottom: theme.border,
      borderColor: `${theme.transparentBorder} !important`,
      margin: '0 20px',
      padding: '15px 0',
    },
    avatar: {
      height: 54,
      width: 54,
      border: `2px solid ${theme.primaryDark}`,
      '& .st0': {
        fill: theme.white,
      },
    },
    avatarIcon: {
      width: '100%',
      height: '100%',
      fill: theme.white,
    },
    greeting: {
      color: theme.white,
      fontSize: theme.fontSizes.default,
      fontWeight: theme.fontWeights.regular,
      lineHeight: 1.7,
    },
    email: {
      fontSize: theme.fontSizes.smaller,
    },
    confirmEmail: {
      fontSize: theme.fontSizes.smaller,
    },
  },
}))

const Menu = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const openUpgradeModal = useUpgradeModal()

  const menuId = product === 'orgamax' ? 'menuOrgamax' : 'menuMeinBuero'
  const { subscriptions } = useSelector(state => state.subscription.details)
  const {
    notificationOverlayOpen,
    searchOverlayOpen,
    menuOverlayOpen,
  } = useSelector(state => state.menu.main)
  const { end: subscriptionEnd } = _.orderBy(subscriptions, ['end'], ['desc'])[0] || {}
  const remainingDays = subscriptionEnd ? Math.max(moment(subscriptionEnd).diff(moment(), 'days'), 0) : ''
  const isTrial = (!user.isDemo && !user.subscriptions?.some(s => s.isTrial === false))
  const isTopPanelVisible = isTrial || user.isDemo || remainingDays < 15

  const classes = useStyles({ isTopPanelVisible, isMeinBuero })

  const [menuBarOpen, setMenuBarOpen] = useState(false)
  const [subMenuBarOpen, setSubMenuBarOpen] = useState(false)

  useEffect(() => {
    return () => {
      closeMenuOverlays()
    }
  }, [])

  const closeMenuOverlays = () => {
    if (notificationOverlayOpen) dispatch(toggleNotificationOverlay(false))
    else if (searchOverlayOpen) dispatch(toggleSearchOverlay(false))
    else if (menuOverlayOpen) dispatch(toggleMenuOverlay(false))
  }

  const handleMouseLeave = () => {
    dispatch(setSubmenuState(false))
  }

  const onLogoClick = () => {
    navigate('/dashboard')
  }

  const toggleHamburger = () => {
    setMenuBarOpen(!menuBarOpen)
  }

  const handleMenuItemClick = () => {
    setSubMenuBarOpen(true)
  }
  const toggleSubMenuBar = () => {
    setSubMenuBarOpen(!subMenuBarOpen)
    dispatch(resetTemporaryActiveMenuItem())
  }

  const handleTrialExpiredClick = () => {
    if (ENV.product === 'orgamax') {
      GtmService.pushEvent({
        event: GtmEventTypes.GaEventHub,
        eventCategory: 'tester-zu-kauf_funnel',
        eventAction: 'click_testphase',
        eventLabel: 'testphase_beenden_badge',
      })
    }
    openUpgradeModal({
      trialExpired: false,
    })
  }

  const topPanelTexts = {
    text: () => {
      if (isTrial) return remainingDays > 0 ? lang.footer.trialText(remainingDays) : lang.footer.trialExpiredText
      else if (remainingDays < 15) return remainingDays > 0 ? lang.footer.subscriptionText(remainingDays) : lang.footer.subscriptionExpired
    },
    buttonLabel: () => {
      if (isTrial) return lang.registerButton
      else if (remainingDays < 15) return lang.upgradeSubscriptionSelectionButtonLabel
    },
  }

  const topPanel = (isTrial || remainingDays < 15)
  && (<TopPanel
    text={topPanelTexts.text()}
    buttonLabel={topPanelTexts.buttonLabel()}
    position='top'
    contentClass={classes.upgradeTopPanel}
    buttonClass={classes.button}
    onClick={handleTrialExpiredClick}
  />)

  const desktopMenu = (
    <>
      <MenuV2 />
      <NotificationDrawer open={notificationOverlayOpen} onClose={closeMenuOverlays} />
      <SearchDrawer open={searchOverlayOpen} onClose={closeMenuOverlays} />
    </>
  )

  const mobileMenu = (
    <>
      {topPanel}
      <div className={classes.mobileContainer}>
        <nav id={menuId} onMouseLeave={handleMouseLeave}>
          <MenuOverlay
            open={notificationOverlayOpen || searchOverlayOpen}
            closeMenuOverlay={closeMenuOverlays}
            isTopPanelVisible={isTopPanelVisible}
          >
            {notificationOverlayOpen && <Notifications />}
            {searchOverlayOpen && <GlobalSearch />}
          </MenuOverlay>
          <div className={classes.mobileNav}>
            <div>
              <div className={clsx(classes.hamburger, menuBarOpen && classes.selected)} onClick={toggleHamburger}>
                <span className={classes.span}></span>
                <span className={classes.span}></span>
                <span className={classes.span}></span>
              </div>
              <SVGIcon svg={LogoSvg} onClick={onLogoClick} className={clsx(classes.logo, menuBarOpen && classes.logoSlide)} width='auto' height='auto' />
            </div>
            <MenuHeader
              toggleMenuOverlay={() => dispatch(toggleMenuOverlay(!menuOverlayOpen))}
              menuOverlayOpen={menuOverlayOpen}
              closeMenuOverlay={closeMenuOverlays}
              className={clsx(menuBarOpen && classes.fadeOut)}
            />
          </div>
        </nav>
        <SwipeDrawer
          open={subMenuBarOpen}
          toggleDrawer={toggleSubMenuBar}
          anchor='left'
          rootClass={classes.subMenuOverlay}
          className={classes.menuDrawer}
          paperClass={classes.paperClass}
          dataQsId='menu-submenu-swipeDrawer'>
          <SubmenuBar toggleSubMenuBar={toggleSubMenuBar} />
        </SwipeDrawer>
        <SwipeDrawer open={menuBarOpen} toggleDrawer={toggleHamburger} anchor='left' className={classes.menuDrawer} paperClass={classes.paperClass} dataQsId='menu-swipeDrawer'>
          <div>
            <UserHeader customClass={userHeaderStyles()} />
            <MenuBar handleMenuItemClick={handleMenuItemClick} />
            <MenuFooter handleMenuItemClick={handleMenuItemClick} />
          </div>
          <div className={classes.imprint}>
            <SubmenuFooter />
          </div>
        </SwipeDrawer>
      </div>
    </>
  )

  return isMobileScreen() ? mobileMenu : desktopMenu
}

const userHeaderStyles = makeStyles(theme => ({
  [theme.devices.mobile]: {
    header: {
      borderTop: theme.border,
      borderBottom: theme.border,
      borderColor: `${theme.transparentBorder} !important`,
      margin: '0 20px',
      padding: '15px 0',
    },
    avatar: {
      height: 54,
      width: 54,
      border: `2px solid ${theme.primaryDark}`,
      '& .st0': {
        fill: theme.white,
      },
    },
    avatarIcon: {
      width: '100%',
      height: '100%',
      fill: theme.white,
    },
    greeting: {
      color: theme.white,
      fontSize: theme.fontSizes.default,
      fontWeight: theme.fontWeights.regular,
      lineHeight: 1.7,
    },
    email: {
      fontSize: theme.fontSizes.smaller,
    },
    confirmEmail: {
      fontSize: theme.fontSizes.smaller,
    },
  },
}))

export default Menu
